import ReactDOM from "react-dom";
import { locales, localeIndexOfLowercase, localeCompareNumericLowercase } from "@ardoq/locale";
import libPluralize from "pluralize";
import { jsx, Fragment } from "react/jsx-runtime";
import { forwardRef } from "react";
import { keyBy, isNil } from "lodash";
import { UAParser } from "my-ua-parser";
import { SortOrder } from "@ardoq/api-types";
import { parseToTimestamp } from "@ardoq/date-time";
import { NumericSortOrder } from "@ardoq/data-model";
import { NumericSortOrder as NumericSortOrder2 } from "@ardoq/data-model";
const createFifoCache = (limit, createValue) => {
  const map = /* @__PURE__ */ new Map();
  return (key) => {
    if (map.has(key)) {
      return map.get(key);
    }
    const value = createValue(key);
    map.set(key, value);
    if (map.size > limit) {
      const [firstKey] = map.keys();
      map.delete(firstKey);
    }
    return value;
  };
};
const isTruthy = Boolean;
var MouseButton = /* @__PURE__ */ ((MouseButton2) => {
  MouseButton2[MouseButton2["PRIMARY"] = 0] = "PRIMARY";
  MouseButton2[MouseButton2["SECONDARY"] = 2] = "SECONDARY";
  return MouseButton2;
})(MouseButton || {});
const Portal = ({
  children,
  to: container = document.body
}) => ReactDOM.createPortal(children, container);
var PluralOperand = /* @__PURE__ */ ((PluralOperand2) => {
  PluralOperand2["ZERO"] = "zero";
  PluralOperand2["ONE"] = "one";
  PluralOperand2["TWO"] = "two";
  PluralOperand2["FEW"] = "few";
  PluralOperand2["MANY"] = "many";
  PluralOperand2["OTHER"] = "other";
  return PluralOperand2;
})(PluralOperand || {});
var CommaStyle = /* @__PURE__ */ ((CommaStyle2) => {
  CommaStyle2["OXFORD"] = "OXFORD";
  CommaStyle2["CAMBRIDGE"] = "CAMBRIDGE";
  return CommaStyle2;
})(CommaStyle || {});
var LastItemConjuction = /* @__PURE__ */ ((LastItemConjuction2) => {
  LastItemConjuction2["AND"] = "and";
  LastItemConjuction2["OR"] = "or";
  return LastItemConjuction2;
})(LastItemConjuction || {});
const ensureString = (str) => str !== null && str !== void 0 ? String(str) : "";
const escapeJavascriptColon = (str) => ensureString(str).replace(/javascript:/gi, "javascript%3");
const ensureAbsolutePathUrl = (url) => url.includes("://") || url.startsWith("//") ? url : `//${url}`;
const truncate = (value, length = 200) => value.length > length ? `${value.substring(0, length)}...` : value;
const capitalize = (str) => str.slice(0, 1).toUpperCase() + str.slice(1);
const pluralize = (word, count) => libPluralize(word, count);
const pluralizeWithCount = (word, count) => libPluralize(word, count, true);
const unpluralizeVerb = (verb, count) => count === 1 ? `${verb}s` : verb;
const getHasOnlyWhiteCharacters = (str) => typeof str === "string" && str.trim() === "" || false;
const pickCommaStyle = (numberOfListedItems, conjunction, commaStyle) => {
  if (numberOfListedItems <= 2) return ` ${conjunction}`;
  switch (commaStyle) {
    case "CAMBRIDGE":
      return ` ${conjunction},`;
    case "OXFORD":
    default:
      return `, ${conjunction}`;
  }
};
const formatListAsEnglishProse = (inputList, conjunction = "and", commaStyle = "OXFORD") => {
  if (inputList.length === 0) return "";
  if (inputList.length === 1) return inputList[0];
  const innerCopy = inputList.slice();
  const maybeConjunctionWithComma = pickCommaStyle(
    innerCopy.length,
    conjunction,
    commaStyle
  );
  const last = innerCopy.pop();
  return `${innerCopy.join(", ")}${maybeConjunctionWithComma} ${last}`;
};
const interpolate = (message, interpolations) => Object.entries(interpolations).reduce(
  (interpolated, [key, value]) => interpolated.replace(new RegExp(`{*${key}*}`, "g"), value),
  message
);
const pickCorrectPlural = (options, count, locale = locales.enGB) => {
  const operand = new Intl.PluralRules(locale.code).select(count);
  return options[operand] || options[
    "one"
    /* ONE */
  ];
};
const tokenIsString = (token) => typeof token === "string";
const getInitials = (name) => {
  if (!name) return "";
  const initials = name.trim().split(" ").map((part) => part.charAt(0));
  const first = initials.at(0);
  const last = initials.length > 1 ? initials.at(-1) : null;
  return `${first ?? ""}${last ?? ""}`;
};
const caseInsensitiveStringIncludes = (stringToSearch, searchString) => stringToSearch.toLowerCase().includes(searchString.toLowerCase());
const stringUtils = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  CommaStyle,
  LastItemConjuction,
  PluralOperand,
  capitalize,
  caseInsensitiveStringIncludes,
  ensureAbsolutePathUrl,
  ensureString,
  escapeJavascriptColon,
  formatListAsEnglishProse,
  getHasOnlyWhiteCharacters,
  getInitials,
  interpolate,
  pickCorrectPlural,
  pluralize,
  pluralizeWithCount,
  tokenIsString,
  truncate,
  unpluralizeVerb
}, Symbol.toStringTag, { value: "Module" }));
const sleep = (durationMs) => new Promise((resolve) => setTimeout(resolve, durationMs));
const promiseNoop = async () => {
  await sleep(700);
};
var SortAttribute = /* @__PURE__ */ ((SortAttribute2) => {
  SortAttribute2["ORDER"] = "_order";
  SortAttribute2["NAME"] = "name";
  SortAttribute2["SOURCE"] = "source";
  SortAttribute2["TARGET"] = "target";
  SortAttribute2["TYPE"] = "type";
  return SortAttribute2;
})(SortAttribute || {});
const escapeRegExp = (value) => /[-[\]{}()*+!<=:?.\\^$|,]/g.test(value) ? value.replace(/[-[\]{}()*+!<=:?.\\^$|,]/g, "\\$&") : value;
const findMatchGroups = (value, searchPhrase, locale) => {
  const index = localeIndexOfLowercase(value, searchPhrase, locale);
  const lastIndex = index + searchPhrase.length;
  if (index === -1) return { preMatch: value, match: "", postMatch: "" };
  return {
    preMatch: value.slice(0, index),
    match: value.slice(index, lastIndex),
    postMatch: value.slice(lastIndex)
  };
};
const classes = (...classNames) => classNames.filter(Boolean).join(" ");
const mapProps = (mapFn) => (WrappedComponent) => forwardRef((props, ref) => /* @__PURE__ */ jsx(
  WrappedComponent,
  {
    ...{ ...props, ...mapFn(props) },
    ref
  }
));
const isShiftXUrl = (url) => url.startsWith("/shiftx/processes/");
const processUrl = (url) => ensureAbsolutePathUrl(escapeJavascriptColon(url));
const maybeRelativeUrl = (val, relative) => relative ? val : processUrl(val);
const sanitizeUrl = (url) => {
  return url.replace(/[^-A-Za-z0-9+&@#/%?=~_|!:,.;()]/g, "");
};
const isValidRedirectUrl = (url, origin, logError) => {
  if (!url) {
    return false;
  }
  try {
    const newUrl = new URL(url, origin);
    return newUrl.origin === origin;
  } catch (e) {
    const error = new Error(e);
    if (logError) {
      logError(error, "Invalid redirect URL", {
        invalidRedirectUrl: url,
        errorMessage: error.message
      });
    }
    return false;
  }
};
const openUrlInNewTab = (targetURL) => {
  window.open(
    maybeRelativeUrl(targetURL, isShiftXUrl(targetURL)),
    "_blank",
    "noopener"
  );
};
const urlUtil = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  isValidRedirectUrl,
  maybeRelativeUrl,
  openUrlInNewTab,
  processUrl,
  sanitizeUrl
}, Symbol.toStringTag, { value: "Module" }));
const closest = (eventTarget, selector) => eventTarget instanceof Element ? eventTarget.closest(selector) : null;
const clearSelection = () => {
  let selection = null;
  if (window.getSelection) {
    selection = window.getSelection();
  } else if (document.selection) {
    selection = document.selection;
  }
  if (selection) {
    if (selection.empty) {
      selection.empty();
    }
    if (selection.removeAllRanges) {
      selection.removeAllRanges();
    }
  }
};
const isJust = (x) => {
  return x !== null;
};
const toByIdDictionary = (entityList) => {
  return keyBy(entityList, "_id");
};
const toggleElementInArray = (collection, item, idBy) => {
  let comparator;
  if (idBy === void 0) {
    comparator = (currentItem) => currentItem === item;
  } else {
    comparator = (currentItem) => currentItem[idBy] === item[idBy];
  }
  const index = collection.findIndex(comparator);
  if (index !== -1) {
    return collection.filter((current) => !comparator(current));
  }
  return [...collection, item];
};
const getArrayDimensions = (array) => {
  const dimensions = [array.length];
  const firstItem = array[0];
  if (!Array.isArray(firstItem)) {
    return dimensions;
  }
  return [array.length, ...getArrayDimensions(firstItem)];
};
const getArrayDimensionsForDisplay = (array) => {
  return `(${getArrayDimensions(array).join(",")})`;
};
const isMac = () => window.navigator.platform.includes("Mac");
let scrollbarWidth;
const getScrollbarWidth = () => {
  if (scrollbarWidth !== void 0) {
    return scrollbarWidth;
  }
  const el = document.createElement("div");
  el.style.overflowY = "scroll";
  el.style.height = "100px";
  const childEl = document.createElement("div");
  childEl.style.height = "200px";
  el.appendChild(childEl);
  document.body.appendChild(el);
  scrollbarWidth = el.offsetWidth - el.clientWidth;
  el.remove();
  return scrollbarWidth;
};
const uaParser$1 = new UAParser();
const browser = uaParser$1.getBrowser();
const engine = uaParser$1.getEngine();
const os = uaParser$1.getOS();
const isMozilla = () => engine.name === "Gecko";
const isSafari = () => browser.name === "Safari";
const isEdge = () => browser.name === "Edge";
const getBrowserPlatform = () => os.name;
const getBrowserVersionNumber = () => browser.version;
const getBrowserName = () => browser.name;
const deepFreeze = (obj) => {
  if (obj === null || obj === void 0) {
    return obj;
  }
  Object.freeze(obj);
  Object.values(obj).forEach((value) => {
    if (value !== null && typeof value === "object") {
      deepFreeze(value);
    }
  });
  return obj;
};
const uaParser = new UAParser();
const device = uaParser.getDevice();
const isMobile = () => device.type === "mobile";
const isTablet = () => device.type === "tablet";
const isComputer = () => device.type === void 0;
const naiveCompare = (a, b) => {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
};
const comparePrimitiveValuesOrDates = (a, b, locale) => {
  if (!isNil(a) && isNil(b)) return 1;
  if (isNil(a) && !isNil(b)) return -1;
  const bothString = typeof a === "string" && typeof b === "string";
  if (bothString) return localeCompareNumericLowercase(a, b, locale);
  return naiveCompare(a, b);
};
const ordinalSuffixOf = /* @__PURE__ */ (() => {
  const suffices = /* @__PURE__ */ new Map([
    [1, "st"],
    [2, "nd"],
    [3, "rd"]
  ]);
  return (n) => {
    const i = n % 10;
    const suffix = (n % 100 / 10 | 0) !== 1 && suffices.get(i) || "th";
    return `${n}${suffix}`;
  };
})();
const getIsOrgAdmin = (currentUser) => {
  var _a;
  const orgLabel = (_a = currentUser.organization) == null ? void 0 : _a.label;
  if (orgLabel === void 0) return false;
  const roles = currentUser.roles;
  if (roles === void 0) return false;
  return roles.includes(`${orgLabel}/admin`);
};
const ADD_TO_PRESENTATION = "Add to presentation";
const WILDCARD = "*";
const returnPayload = (_state, payload) => payload;
const spreadPayloadOverState = (state, payload) => ({ ...state, ...payload });
const returnFalse = () => false;
const returnTrue = () => true;
const returnNull = () => null;
const returnZero = () => 0;
const setStateProperty = (key) => {
  return function setStatePropertySetter(state, value) {
    return { ...state, [key]: value };
  };
};
const alterSortOrder = (sortOrder) => {
  if (sortOrder === SortOrder.DESC) return SortOrder.DEFAULT;
  if (sortOrder === SortOrder.ASC) return SortOrder.DESC;
  return SortOrder.ASC;
};
const findSortOrder = (sortOrder, currentSortByField, newSortByField) => {
  if (currentSortByField === newSortByField) {
    return alterSortOrder(sortOrder);
  }
  return SortOrder.ASC;
};
const orderUnsetValues = ({
  aIsNil,
  bIsNil,
  orderUnsetLast
}) => {
  const orderFactor = orderUnsetLast ? -1 : 1;
  if (!aIsNil && bIsNil) {
    return 1 * orderFactor;
  }
  if (aIsNil && !bIsNil) {
    return -1 * orderFactor;
  }
  return 0;
};
const isComparableNumericValue = (value) => typeof value === "number" || typeof value === "boolean";
const compareNumericValuesAsc = (a, b) => a - b;
const applySortOrder = (comparisonResult, sortOrder) => comparisonResult && // prevents negative zero
comparisonResult * (sortOrder === NumericSortOrder.DESC ? -1 : 1);
const validDateFieldValueToDateNumber = (value) => {
  if (isNil(value)) {
    return value;
  }
  if (value instanceof Date) {
    return value.getTime();
  }
  return parseToTimestamp(value);
};
const getDateRangeValuesForPotentiallyInfiniteDateRanges = (componentAttributes, [startFieldName, endFieldName]) => {
  const start = validDateFieldValueToDateNumber(
    componentAttributes[startFieldName]
  );
  const end = validDateFieldValueToDateNumber(
    componentAttributes[endFieldName]
  );
  if (start && end === null) {
    return [start, Infinity];
  }
  if (end && start === null) {
    return [-Infinity, end];
  }
  return [start, end];
};
const compareComponentsByDateRangeFields = ({
  componentAttributesA,
  componentAttributesB,
  dateRangeFieldNames: [startFieldName, endFieldName],
  userLocale,
  sort
}) => {
  const [startValueA, endValueA] = getDateRangeValuesForPotentiallyInfiniteDateRanges(componentAttributesA, [
    startFieldName,
    endFieldName
  ]);
  const [startValueB, endValueB] = getDateRangeValuesForPotentiallyInfiniteDateRanges(componentAttributesB, [
    startFieldName,
    endFieldName
  ]);
  return comparePrimitiveValues(startValueA, startValueB, sort, userLocale) || comparePrimitiveValues(endValueA, endValueB, sort, userLocale) || comparePrimitiveValues(
    componentAttributesA.name,
    componentAttributesB.name,
    sort,
    userLocale
  );
};
const comparePrimitiveValues = (valueA, valueB, { order, orderUnsetLast }, userLocale) => {
  const aIsNil = isNil(valueA);
  const bIsNil = isNil(valueB);
  if (aIsNil || bIsNil) {
    return orderUnsetValues({
      aIsNil,
      bIsNil,
      orderUnsetLast
    });
  }
  if (typeof valueA === "string" && typeof valueB === "string") {
    return applySortOrder(
      localeCompareNumericLowercase(valueA, valueB, userLocale),
      order
    );
  }
  if (isComparableNumericValue(valueA) && isComparableNumericValue(valueB)) {
    return applySortOrder(compareNumericValuesAsc(valueA, valueB), order);
  }
  return 0;
};
const getContextSortAttributeName = (sortAttr) => sortAttr === SortAttribute.SOURCE || sortAttr === SortAttribute.TARGET ? SortAttribute.NAME : sortAttr;
const compareAttributes = (componentAttributesA, componentAttributesB, sort, userLocale) => {
  const attributeName = getContextSortAttributeName(sort.attr);
  const rawValueA = componentAttributesA[attributeName];
  const rawValueB = componentAttributesB[attributeName];
  const valueA = rawValueA instanceof Date ? rawValueA.valueOf() : rawValueA;
  const valueB = rawValueB instanceof Date ? rawValueB.valueOf() : rawValueB;
  if (valueA === valueB || !valueA && !valueB) {
    return localeCompareNumericLowercase(
      componentAttributesA.name,
      componentAttributesB.name,
      userLocale
    );
  }
  return comparePrimitiveValues(valueA, valueB, sort, userLocale);
};
const compareComponentsById = (componentIdA, componentIdB, sortDependencies) => {
  const { componentAttributesById, sort, userLocale, isDateRangeField } = sortDependencies;
  const componentAttributesA = componentAttributesById[componentIdA];
  const componentAttributesB = componentAttributesById[componentIdB];
  if (isDateRangeField) {
    return compareComponentsByDateRangeFields({
      componentAttributesA,
      componentAttributesB,
      dateRangeFieldNames: sort.attr.split(","),
      userLocale,
      sort
    });
  }
  return compareAttributes(
    componentAttributesA,
    componentAttributesB,
    sort,
    userLocale
  );
};
class ArdoqError {
  constructor(error, userMessage, traceId, isAborted) {
    this.error = error;
    this.userMessage = userMessage;
    this.traceId = traceId;
    this.isAborted = isAborted;
  }
}
const isArdoqError = (possibleError) => possibleError instanceof ArdoqError;
const toArdoqError = ({
  error,
  userMessage,
  traceId,
  isAborted
}) => new ArdoqError(error, userMessage, traceId, isAborted);
const getArdoqErrorMessage = (error, fallbackMessage) => {
  return error.userMessage ?? fallbackMessage ?? error.error.message;
};
const getErrorForLogging = (error) => {
  return error.error;
};
const getArdoqErrorTraceId = (error) => {
  return error.traceId ?? "";
};
const resultsToPayloadOrError = (results, message) => {
  const ardoqErrors = results.filter(isArdoqError);
  if (ardoqErrors.length) {
    const errors = ardoqErrors.map(getErrorForLogging);
    const error = new AggregateError(
      errors,
      message ?? getArdoqErrorMessage(ardoqErrors[0])
    );
    return toArdoqError({
      error
    });
  }
  return results;
};
const exhaustiveCheck = (param) => {
  throw new Error(`should not reach here with ${param}`);
};
const pipeReducers = (state, ...pipe) => pipe.reduce((intermediateState, reduce) => reduce(intermediateState), state);
const partialPayload = (reducer, ...payload) => (state) => reducer(state, ...payload);
const filterObject = (obj, predicate) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([key, value]) => predicate(key, value))
  );
};
const getAddToMaps = ({ referenceMap, sourceMap, targetMap }) => ({ referenceId, sourceId, targetId }) => {
  {
    referenceMap.set(referenceId, { sourceId, targetId });
    let targetIds = sourceMap.get(sourceId);
    if (!targetIds) {
      targetIds = [];
      sourceMap.set(sourceId, targetIds);
    }
    targetIds.push({ componentId: targetId, referenceId });
    let sourceIds = targetMap.get(targetId);
    if (!sourceIds) {
      sourceIds = [];
      targetMap.set(targetId, sourceIds);
    }
    sourceIds.push({ componentId: sourceId, referenceId });
  }
};
const HighlightedText = ({
  text,
  highlight
}) => {
  if (!highlight || !text || text.toLocaleLowerCase().indexOf(highlight.toLocaleLowerCase()) === -1) {
    return text;
  }
  const lowerText = text.toLocaleLowerCase();
  const lowerHighlight = highlight.toLocaleLowerCase();
  const highlightLen = lowerHighlight.length;
  const build = (startIndex) => {
    const matchIndex = lowerText.indexOf(lowerHighlight, startIndex);
    if (matchIndex === -1) {
      return startIndex < text.length ? [text.substring(startIndex)] : [];
    }
    const before = matchIndex > startIndex ? [text.substring(startIndex, matchIndex)] : [];
    const matched = /* @__PURE__ */ jsx("b", { children: text.substring(matchIndex, matchIndex + highlightLen) });
    const after = build(matchIndex + highlightLen);
    return [...before, matched, ...after];
  };
  return /* @__PURE__ */ jsx(Fragment, { children: build(0) });
};
const resetState = (state) => function resetStateSetter() {
  return state;
};
const updateState = (partialState) => function updateStateSetter(state) {
  return {
    ...state,
    ...partialState
  };
};
const ARDOQ_ID_LENGTH = 24;
const ardoqId = () => {
  const dateInHexCode = (/* @__PURE__ */ new Date()).getTime().toString(16);
  return [
    dateInHexCode,
    ...Array(ARDOQ_ID_LENGTH - dateInHexCode.length).fill("").map(() => Math.floor(Math.random() * 16).toString(16))
  ].join("");
};
export {
  ADD_TO_PRESENTATION,
  ARDOQ_ID_LENGTH,
  CommaStyle,
  HighlightedText,
  LastItemConjuction,
  MouseButton,
  NumericSortOrder2 as NumericSortOrder,
  PluralOperand,
  Portal,
  SortAttribute,
  WILDCARD,
  alterSortOrder,
  applySortOrder,
  ardoqId,
  capitalize,
  caseInsensitiveStringIncludes,
  classes,
  clearSelection,
  closest,
  compareComponentsByDateRangeFields,
  compareComponentsById,
  comparePrimitiveValues,
  comparePrimitiveValuesOrDates,
  createFifoCache,
  deepFreeze,
  ensureAbsolutePathUrl,
  ensureString,
  escapeJavascriptColon,
  escapeRegExp,
  exhaustiveCheck,
  filterObject,
  findMatchGroups,
  findSortOrder,
  formatListAsEnglishProse,
  getAddToMaps,
  getArdoqErrorMessage,
  getArdoqErrorTraceId,
  getArrayDimensions,
  getArrayDimensionsForDisplay,
  getBrowserName,
  getBrowserPlatform,
  getBrowserVersionNumber,
  getContextSortAttributeName,
  getDateRangeValuesForPotentiallyInfiniteDateRanges,
  getErrorForLogging,
  getHasOnlyWhiteCharacters,
  getInitials,
  getIsOrgAdmin,
  getScrollbarWidth,
  interpolate,
  isArdoqError,
  isComparableNumericValue,
  isComputer,
  isEdge,
  isJust,
  isMac,
  isMobile,
  isMozilla,
  isSafari,
  isShiftXUrl,
  isTablet,
  isTruthy,
  isValidRedirectUrl,
  mapProps,
  openUrlInNewTab,
  orderUnsetValues,
  ordinalSuffixOf,
  partialPayload,
  pickCorrectPlural,
  pipeReducers,
  pluralize,
  pluralizeWithCount,
  promiseNoop,
  resetState,
  resultsToPayloadOrError,
  returnFalse,
  returnNull,
  returnPayload,
  returnTrue,
  returnZero,
  setStateProperty,
  sleep,
  spreadPayloadOverState,
  stringUtils,
  toArdoqError,
  toByIdDictionary,
  toggleElementInArray,
  tokenIsString,
  truncate,
  unpluralizeVerb,
  updateState,
  urlUtil as urlUtils,
  validDateFieldValueToDateNumber
};
//# sourceMappingURL=index.js.map
